import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import { useUpdateProfile } from '../api/account.queries';
import { profileSchema, type ProfileFormValues } from '../schemas/profile.schema';

/**
 * Your own display name — what other staff see in the audit log, assignments
 * and approvals. Username, email and mobile are sign-in identity and are
 * changed by an administrator, not here.
 */
export function ProfileDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const mutation = useUpdateProfile();
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name },
  });

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors?.name) return;
    form.setError('name', { type: 'server', message: error.fieldErrors.name });
  }, [error, form]);

  const formError =
    error instanceof ApiError && !error.fieldErrors?.name
      ? error.message
      : error && !(error instanceof ApiError)
        ? 'Could not save your name.'
        : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Profile</DialogTitle>
          <DialogDescription className="text-xs">
            Your name as other staff see it. Your username stays the same.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            noValidate
            className="space-y-4"
            onSubmit={form.handleSubmit((values) =>
              mutation.mutate(values.name.trim(), {
                onSuccess: (changed) => {
                  if (changed) toast.success('Name saved.');
                  onClose();
                },
              }),
            )}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="name" maxLength={50} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {formError && (
              <p role="alert" className="text-destructive text-xs">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
